import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DEFAULT_CACHE_TIME_MS,
  QUERY_WHITELIST,
} from '../../apis/moovyQueryKeys'
import {
  CreateWhitelistedVehicleDTO,
  WhitelistSortType,
  WhitelistedVehicleDTO,
} from '../../apis/whitelist/dto'
import {
  MoovyButton,
  MoovyContainer,
  MoovyContainerContent,
  MoovyContainerHeader,
  MoovyContainerSubHeader,
  MoovyScrollToTop,
} from '../../components'

import Typography from '@mui/material/Typography'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { getDefaultPaginationAndSortingValues } from '../../apis'
import { WhitelistApi } from '../../apis/whitelist/whitelistApi'
import { hasPermission } from '../../app/permissions'
import { PaginationAndSortingValues } from '../../components/moovyPagination'
import useAuth from '../../hooks/useAuth'
import { MoovyToast } from '../../utils/moovyToast'
import { FormValues } from './formWhitelistedVehicle'
import ModalWhitelistedVehicle from './modalWhitelistedVehicle'
import WhitelistTable from './whitelistTable'

export type WhitelistSearchParameters = {
  search: string
}

const Whitelist = () => {
  const { isAdmin } = useAuth()
  const queryClient = useQueryClient()

  const defaultSortCriteria: WhitelistSortType = 'NAME'
  const [paginationAndSorting, setPaginationAndSorting] =
    useState<PaginationAndSortingValues>({
      ...getDefaultPaginationAndSortingValues(defaultSortCriteria),
    })
  const [searchParameters, setSearchParameters] =
    useState<WhitelistSearchParameters>({
      search: '',
    })

  const queryKey = [
    QUERY_WHITELIST,
    searchParameters.search,
    paginationAndSorting.page,
    paginationAndSorting.sort,
    paginationAndSorting.direction,
  ]

  const queryWhitelist = useQuery({
    queryKey,
    queryFn: () => {
      return WhitelistApi.getWhitelist({
        paginationAndSorting,
        searchParameters,
      })
    },
    staleTime: DEFAULT_CACHE_TIME_MS,
  })

  const [selectedWhitelistedVehicle, setSelectedWhitelistedVehicle] =
    useState<WhitelistedVehicleDTO | null>(null)
  const [modalWhitelistedVehicleVisible, setModalWhitelistedVehicleVisible] =
    useState(false)

  const invalidateQuery = () => {
    queryClient.invalidateQueries({
      queryKey,
    })
  }

  const createWhitelistedVehicleMutation = useMutation({
    mutationFn: (vehicleData: CreateWhitelistedVehicleDTO) =>
      WhitelistApi.createWhitelistedVehicle(vehicleData),
    onSuccess: () => {
      invalidateQuery()
      MoovyToast.showToast(
        <FormattedMessage id="page.whitelist.modal.create.success" />,
        'success',
      )
      onCloseWhitelistedVehicleModal()
    },
  })

  const updateWhitelistedVehicleMutation = useMutation({
    mutationFn: (vehicle: CreateWhitelistedVehicleDTO) => {
      // Should never happen; if no vehicle is selected, the modal should be
      // closed or the submit button should call the create mutation
      if (!selectedWhitelistedVehicle) {
        MoovyToast.showToast(
          <FormattedMessage
            id="page.whitelist.modal.edit.error"
            values={{ plate: vehicle.licensePlate }}
          />,
          'error',
        )
        throw new Error('No selected whitelisted vehicle')
      }
      return WhitelistApi.updateWhitelistedVehicle({
        id: selectedWhitelistedVehicle.id,
        vehicleData: vehicle,
        comment: vehicle.comment,
      })
    },
    onSuccess: () => {
      invalidateQuery()
      MoovyToast.showToast(
        <FormattedMessage
          id="page.whitelist.modal.edit.success"
          values={{ plate: selectedWhitelistedVehicle?.licensePlate }}
        />,
        'success',
      )
      onCloseWhitelistedVehicleModal()
    },
  })

  const onCloseWhitelistedVehicleModal = () => {
    updateWhitelistedVehicleMutation.reset()
    createWhitelistedVehicleMutation.reset()
    setModalWhitelistedVehicleVisible(false)
    setSelectedWhitelistedVehicle(null)
  }

  const deleteWhitelistedVehicleMutation = useMutation({
    mutationFn: (parameters: { id: string; comment: string }) =>
      WhitelistApi.deleteWhitelistedVehicle(parameters),
    onSuccess: () => {
      invalidateQuery()
      MoovyToast.showToast(
        <FormattedMessage
          id="page.whitelist.modal.delete.success"
          values={{ plate: selectedWhitelistedVehicle?.licensePlate }}
        />,
        'success',
      )
      onCloseWhitelistedVehicleModal()
    },
  })

  const hasEditPermission = hasPermission(isAdmin, 'EDIT', 'WHITELIST')
  const onClickRow = (whitelistedVehicle: WhitelistedVehicleDTO) => {
    if (hasEditPermission) {
      setSelectedWhitelistedVehicle(whitelistedVehicle)
      setModalWhitelistedVehicleVisible(true)
    }
  }

  const whitelist: WhitelistedVehicleDTO[] =
    queryWhitelist.data?.data.data || []
  return (
    <>
      <MoovyScrollToTop />
      <MoovyContainer loading={queryWhitelist.isLoading}>
        <MoovyContainerHeader>
          <FormattedMessage id="common.text.whitelist" />
        </MoovyContainerHeader>
        <MoovyContainerSubHeader
          description={
            <Typography>
              <FormattedMessage id="page.whitelist.description" />{' '}
              {hasPermission(isAdmin, 'EDIT', 'WHITELIST') ? (
                <FormattedMessage id="page.whitelist.description.create" />
              ) : (
                <FormattedMessage id="page.whitelist.description.readOnly" />
              )}
            </Typography>
          }
          button={
            hasPermission(isAdmin, 'CREATE', 'WHITELIST') && (
              <MoovyButton
                onClick={() => {
                  setSelectedWhitelistedVehicle(null)
                  setModalWhitelistedVehicleVisible(true)
                }}
              >
                {<FormattedMessage id="common.buttons.add" />}
              </MoovyButton>
            )
          }
        />
        <MoovyContainerContent>
          <WhitelistTable
            whitelist={whitelist}
            queryWhitelist={queryWhitelist}
            queryKey={queryKey}
            searchParametersControl={[searchParameters, setSearchParameters]}
            paginationAndSorting={paginationAndSorting}
            setPaginationAndSorting={setPaginationAndSorting}
            onClickRow={onClickRow}
            defaultSortCriteria={defaultSortCriteria}
          />
        </MoovyContainerContent>
      </MoovyContainer>
      <ModalWhitelistedVehicle
        open={modalWhitelistedVehicleVisible}
        vehicle={selectedWhitelistedVehicle}
        onCancel={onCloseWhitelistedVehicleModal}
        mutations={{
          CREATE: createWhitelistedVehicleMutation,
          EDIT: updateWhitelistedVehicleMutation,
          DELETE: deleteWhitelistedVehicleMutation,
        }}
        errorTitleKeys={{
          CREATE: 'page.whitelist.modal.create.error',
          EDIT: 'page.whitelist.modal.edit.error',
          DELETE: 'page.whitelist.modal.delete.error',
        }}
        modalTitleKeys={{
          CREATE: 'page.whitelist.modal.create.title',
          EDIT: 'page.whitelist.modal.edit.title',
          READ: 'page.whitelist.modal.read.title',
        }}
        formTitleKeys={{
          CREATE: 'page.whitelist.form.create.title',
          EDIT: 'page.whitelist.form.edit.title',
          READ: 'page.whitelist.form.read.title',
        }}
        submitButtonContentKeys={{
          CREATE: 'page.whitelist.modal.create.button.submit',
          EDIT: 'page.whitelist.modal.edit.button.submit',
          DELETE: 'common.buttons.remove',
        }}
        onSubmit={(values: FormValues) => {
          if (!selectedWhitelistedVehicle) {
            createWhitelistedVehicleMutation.mutate(
              values as CreateWhitelistedVehicleDTO,
            )
          } else {
            updateWhitelistedVehicleMutation.mutate(
              values as CreateWhitelistedVehicleDTO,
            )
          }
        }}
        onDelete={(values: FormValues) => {
          if (selectedWhitelistedVehicle) {
            deleteWhitelistedVehicleMutation.mutate({
              id: selectedWhitelistedVehicle.id,
              comment: values.comment,
            })
          } else {
            MoovyToast.showToast(
              <FormattedMessage id="common.actions.remove.error" />,
              'error',
            )
          }
        }}
      />
    </>
  )
}

export default Whitelist
